import type { ConnectionOptions, HomeCacheEntry } from "@foreman/app-core";

/**
 * What Home showed last, kept on this phone, so opening the app draws the session list and the team at once and the real
 * answer replaces it quietly. Per computer; nothing older than three days is used; capped in size. It never holds a decision
 * waiting on the person (see `ConnectionOptions.homeCache`).
 */
const KEY = "foreman.home-cache";
const MAX_AGE_MS = 3 * 24 * 3600 * 1000;
const MAX_SESSIONS = 80;
const MAX_BYTES = 350_000;
type Saved = Record<string, HomeCacheEntry>;

const read = (): Saved => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as unknown;
    return raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Saved) : {};
  } catch {
    return {};
  }
};

const write = (all: Saved): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* private mode or a full store: just not remembered */
  }
};

export const homeCache: NonNullable<ConnectionOptions["homeCache"]> = {
  load(computerId) {
    const e = read()[computerId];
    if (!e || !Array.isArray(e.sessions) || typeof e.savedAt !== "number" || Date.now() - e.savedAt > MAX_AGE_MS) return null;
    return e;
  },
  save(computerId, entry) {
    const all = read();
    all[computerId] = { ...entry, sessions: entry.sessions.slice(0, MAX_SESSIONS) };
    if (JSON.stringify(all).length > MAX_BYTES) all[computerId] = { savedAt: entry.savedAt, sessions: entry.sessions.slice(0, 30), office: null };
    write(all);
  },
};

/** Forget what was kept for computers that are no longer paired. */
export function pruneHomeCache(validIds: string[]): void {
  const all = read();
  const keep = Object.fromEntries(Object.entries(all).filter(([id]) => validIds.includes(id)));
  if (Object.keys(keep).length !== Object.keys(all).length) write(keep);
}
